import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { RootState } from "@/store/index"
import axios from "axios"
import moment from "moment"
import {
    EvChargingLoadingTypes,
    EvChargingPricePoint,
    EvChargingSession,
    EvChargingSessionApiResponse,
    EvChargingSessionsApiResponse,
    EvChargingState,
} from "./ev-charging/types"

const STORE_NAME = "evCharging"

export enum EvChargingMutations {
    SET_LOADING = "setLoading",
    SET_SESSIONS = "setSessions",
    SET_CURRENT_SESSION = "setCurrentSession",
    PATCH_CURRENT_SESSION = "patchCurrentSession",
    SET_PRICES = "setPrices",
    SET_REALTIME_POWER_W = "setRealtimePowerW",
}

export const EvChargingActions = {
    FETCH_SESSIONS: `${STORE_NAME}/fetchSessions`,
    FETCH_SESSION: `${STORE_NAME}/fetchSession`,
    FETCH_PRICES: `${STORE_NAME}/fetchPrices`,
    FETCH_REALTIME_POWER: `${STORE_NAME}/fetchRealtimePower`,
    UPDATE_SESSION: `${STORE_NAME}/updateSession`,
    SET_OPTIMIZE_FOR_COST: `${STORE_NAME}/setOptimizeForCost`,
} as const

const state: EvChargingState = {
    sessions: [],
    currentSession: null,
    prices: [],
    realtimePowerW: null,
    loading: {
        [EvChargingLoadingTypes.SESSIONS]: false,
        [EvChargingLoadingTypes.SESSION]: false,
        [EvChargingLoadingTypes.UPDATE_SESSION]: false,
        [EvChargingLoadingTypes.PRICES]: false,
        [EvChargingLoadingTypes.REALTIME_POWER]: false,
    },
}

const mutations: MutationTree<EvChargingState> = {
    [EvChargingMutations.SET_LOADING](
        state,
        payload: { type: EvChargingLoadingTypes; loading: boolean },
    ) {
        state.loading = {
            ...state.loading,
            [payload.type]: payload.loading,
        }
    },
    [EvChargingMutations.SET_SESSIONS](state, sessions: EvChargingSession[]) {
        state.sessions = sessions
    },
    [EvChargingMutations.SET_CURRENT_SESSION](state, session: EvChargingSession | null) {
        state.currentSession = session
    },
    [EvChargingMutations.PATCH_CURRENT_SESSION](state, patch: Partial<EvChargingSession>) {
        if (state.currentSession) {
            state.currentSession = { ...state.currentSession, ...patch }
        }
    },
    [EvChargingMutations.SET_PRICES](state, prices: EvChargingPricePoint[]) {
        state.prices = prices
    },
    [EvChargingMutations.SET_REALTIME_POWER_W](state, powerW: number | null) {
        state.realtimePowerW = powerW
    },
}

const actions: ActionTree<EvChargingState, RootState> = {
    async fetchSessions({ commit }, locationId: number | string) {
        commit(EvChargingMutations.SET_LOADING, {
            type: EvChargingLoadingTypes.SESSIONS,
            loading: true,
        })
        try {
            const res = await axios.get<EvChargingSessionsApiResponse>(
                `/locations/${locationId}/ev-charging-sessions?limit=50&offset=0`,
            )
            commit(EvChargingMutations.SET_SESSIONS, res.data.data)
        } catch (e) {
            console.log(e)
        } finally {
            commit(EvChargingMutations.SET_LOADING, {
                type: EvChargingLoadingTypes.SESSIONS,
                loading: false,
            })
        }
    },

    async fetchSession({ commit }, sessionId: number | string) {
        commit(EvChargingMutations.SET_LOADING, {
            type: EvChargingLoadingTypes.SESSION,
            loading: true,
        })
        try {
            const res = await axios.get<EvChargingSessionApiResponse>(
                `/ev-charging-sessions/${sessionId}`,
            )
            commit(EvChargingMutations.SET_CURRENT_SESSION, res.data.data.session)
        } catch (e) {
            console.log(e)
        } finally {
            commit(EvChargingMutations.SET_LOADING, {
                type: EvChargingLoadingTypes.SESSION,
                loading: false,
            })
        }
    },

    async fetchPrices({ commit }, locationId: number | string) {
        commit(EvChargingMutations.SET_LOADING, {
            type: EvChargingLoadingTypes.PRICES,
            loading: true,
        })
        try {
            const res = await axios.get(`v2/pricing/locations/${locationId}/prices/chart`, {
                params: {
                    from: moment().format("YYYY-MM-DD"),
                    to: moment().add(1, "day").format("YYYY-MM-DD"),
                },
            })
            commit(EvChargingMutations.SET_PRICES, res.data?.data?.prices ?? [])
        } catch (e) {
            console.log(e)
        } finally {
            commit(EvChargingMutations.SET_LOADING, {
                type: EvChargingLoadingTypes.PRICES,
                loading: false,
            })
        }
    },

    async fetchRealtimePower({ commit }, locationId: number | string) {
        try {
            const res = await axios.get(`/locations/${locationId}/statistics`, {
                params: { type: "latest" },
            })
            const power = res.data?.data?.data?.evCharger?.basePower?.value
            commit(EvChargingMutations.SET_REALTIME_POWER_W, power != null ? Math.abs(power) : null)
        } catch (e) {
            console.log(e)
        }
    },

    async updateSession(
        { commit, dispatch },
        payload: {
            sessionId: number
            initialSoc: number
            targetSoc: number
            arrivalAt: string
            departureAt: string
            evBatteryCapacityMax: number
            optimizeForCost: boolean
        },
    ) {
        commit(EvChargingMutations.SET_LOADING, {
            type: EvChargingLoadingTypes.UPDATE_SESSION,
            loading: true,
        })
        try {
            const res = await axios.put<EvChargingSessionApiResponse>(
                `/ev-charging-sessions/${payload.sessionId}`,
                {
                    data: {
                        initialSoc: payload.initialSoc,
                        targetSoc: payload.targetSoc,
                        arrivalAt: payload.arrivalAt,
                        departureAt: payload.departureAt,
                        evBatteryCapacityMax: payload.evBatteryCapacityMax,
                        optimizeForCost: payload.optimizeForCost,
                    },
                },
            )
            commit(EvChargingMutations.SET_CURRENT_SESSION, res.data.data.session)
            dispatch(
                "toastMessageModule/showMessage",
                { message: "ev_charging.edit.save_success", color: "green", isTranslation: true },
                { root: true },
            )
        } catch (e) {
            dispatch(
                "toastMessageModule/showMessage",
                { message: "ev_charging.edit.save_error", color: "red", isTranslation: true },
                { root: true },
            )
            throw e
        } finally {
            commit(EvChargingMutations.SET_LOADING, {
                type: EvChargingLoadingTypes.UPDATE_SESSION,
                loading: false,
            })
        }
    },

    async setOptimizeForCost(
        { commit, state, dispatch },
        payload: { sessionId: number; value: boolean },
    ) {
        const session = state.currentSession
        if (
            !session ||
            session.initialSoc == null ||
            session.targetSoc == null ||
            session.arrivalAt == null ||
            session.departureAt == null ||
            session.evBatteryCapacityMax == null
        ) {
            return
        }

        commit(EvChargingMutations.PATCH_CURRENT_SESSION, { optimizeForCost: payload.value })

        try {
            await axios.put(`/ev-charging-sessions/${payload.sessionId}`, {
                data: {
                    initialSoc: session.initialSoc,
                    targetSoc: session.targetSoc,
                    arrivalAt: session.arrivalAt,
                    departureAt: session.departureAt,
                    evBatteryCapacityMax: session.evBatteryCapacityMax,
                    optimizeForCost: payload.value,
                },
            })
        } catch (e) {
            commit(EvChargingMutations.PATCH_CURRENT_SESSION, {
                optimizeForCost: !payload.value,
            })
            dispatch(
                "toastMessageModule/showMessage",
                { message: "ev_charging.edit.save_error", color: "red", isTranslation: true },
                { root: true },
            )
        }
    },
}

const getters: GetterTree<EvChargingState, RootState> = {
    sessions: ({ sessions }) => sessions,
    activeSessions: ({ sessions }) => sessions.filter((s) => !s.isClosed),
    previousSessions: ({ sessions }) => sessions.filter((s) => s.isClosed),
    currentSession: ({ currentSession }) => currentSession,
    prices: ({ prices }) => prices,
    realtimePowerW: ({ realtimePowerW }) => realtimePowerW,
    loading: ({ loading }) => loading,
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
} as Module<EvChargingState, RootState>
